import { Component, computed, DestroyRef, effect, inject, Signal, signal, ViewChild, AfterViewInit } from '@angular/core';
import { GoogleMap } from '@angular/google-maps';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { PackageService } from '@lib/package/package.service';
import { QueryParamsService } from '@shared/filters/query-params.service';
import { FiltersService } from '@shared/filters/filters.service';
import { combineLatest, debounceTime, distinctUntilChanged, map, Observable, shareReplay, startWith, switchMap, take, timer } from 'rxjs';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { SelectFilter } from '@shared/filters/filter';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { APP_CONSTANTS, DEFAULT_MAP_CENTER, DEFAULT_MAP_ID, DEFAULT_MAP_MAXZOOM, DEFAULT_MAP_MINZOOM, DEFAULT_MAP_ZOOM, posToLatLngLiteral } from '@lib/constants';
import { polarProjectLatLng, QueryParams } from '@lib/api/helpers';
import { Result, toResult } from '@mastechnicaegroup/carelinelive-cdk/rx';
import { PaginatedApiResponse } from '@lib/api/responses/paginated-api-response';
import { Package, PackageAddress } from '@lib/api/models/package';
import { ApiResponse } from '@lib/api/responses/api-response';
import { MarkerClusterer, Renderer } from '@googlemaps/markerclusterer';
import { markerSvg } from '../../../../assets/markers.svg';
import { Cluster } from '@googlemaps/markerclusterer/dist/cluster';
import AdvancedMarkerElement = google.maps.marker.AdvancedMarkerElement;
import { ComponentsModule } from '@mastechnicaegroup/carelinelive-ui';
import { PaginationCardComponent } from '@shared/pagination/pagination-card.component';
import { AsyncPipe, NgIf, NgPlural, NgPluralCase } from '@angular/common';
import { AppDatePipe } from '@shared/pipes/date.pipe';
import { HeaderComponent } from '@shared/layout/header/header.component';
import { FilterChipComponent } from '@shared/filters/filter-chip/filter-chip.component';
import { ErrorPanelComponent } from '@lib/errors/error-panel/error-panel.component';
import { providePackageFilters } from '../index/package-index.filters';
import { AuthenticationService } from '@lib/auth/authentication.service';

@Component({
    selector: 'app-potential-packages',
    imports: [
        ComponentsModule,
        PaginationCardComponent,
        RouterLink,
        AppDatePipe,
        ReactiveFormsModule,
        HeaderComponent,
        GoogleMap,
        NgPlural,
        NgPluralCase,
        FilterChipComponent,
        AsyncPipe,
        NgIf,
        ErrorPanelComponent
    ],
    templateUrl: './potential-packages.component.html',
    standalone: true,
    styleUrl: './potential-packages.component.scss',
    providers: [
        FiltersService,
        providePackageFilters()
    ]
})
export class PotentialPackagesComponent implements AfterViewInit {
    @ViewChild(GoogleMap) googleMapView!: GoogleMap;
    private query_change_count = 0;
    private readonly _activeRoute = inject(ActivatedRoute);
    private readonly _packageService = inject(PackageService);
    private readonly _router = inject(Router);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _authService = inject(AuthenticationService);
    private readonly queryParamsService = inject(QueryParamsService);
    protected readonly filters = inject(FiltersService);

    protected user = toSignal(this._authService.user$);

    private markerLibrary: google.maps.MarkerLibrary | undefined;
    private googleMap: google.maps.Map | undefined;

    filtersVisible = false;

    private readonly intersectionObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
            if (entry.isIntersecting) {
                entry.target.classList.add('drop');
                this.intersectionObserver.unobserve(entry.target);
            }
        }
    });

    // get some filter loading state
    protected optionsLoading$ = combineLatest(this.filters.allFilters.filter(filter => {
        return filter.type === 'select' || filter.type === 'multi-select';
    }).map(filter => {
        return toObservable<boolean>((filter as SelectFilter<unknown>).options.loading);
    })).pipe(
        map(val => val.filter(v => v === true).length > 0)
    );

    // get some filter error state
    protected optionsError$ = combineLatest(this.filters.allFilters.filter(filter => {
        return filter.type === 'select' || filter.type === 'multi-select';
    }).map(filter => {
        return toObservable((filter as SelectFilter<unknown>).options.error);
    })).pipe(
        map(val => val.filter(v => v !== null).length > 0)
    );

    packages?: Record<string, string | number>[];

    /** The type of view list or map */
    protected listType = toSignal<'list' | 'map'>(this._activeRoute.queryParams.pipe(
        map((params) => params['type'] === 'map' ? 'map' : 'list')
    ));

    protected readonly mapError = signal<ErrorEvent | null>(null);

    /** The search form control */
    protected readonly searchControl = new FormControl<string>(
        this._activeRoute.snapshot.queryParams['q'] ?? '',
        [Validators.maxLength(50)]
    );

    /** An observable that emits the search query */
    protected readonly searchQuery = toSignal(this.searchControl.valueChanges.pipe(
        startWith(this.searchControl.value),

        // Only fire once every 100ms
        debounceTime(APP_CONSTANTS.debounceTimeMs),

        // If it's an empty string, return null
        map(value => value ? value : undefined)
    ));

    protected readonly queryParams$: Observable<QueryParams> = combineLatest({
        searchQuery: toObservable(this.searchQuery),
        filterParams: this.filters.filterValueChanged$
    }).pipe(
        map(({ searchQuery, filterParams }) => this.queryParamsService.filterToQueryParams(filterParams, searchQuery)),
        // Share the same result with all new subscribers
        shareReplay(1)
    );

    /** The current page being shown */
    protected readonly page = toSignal<number>(this._activeRoute.queryParams.pipe(
        map((params): number => params['page'] ?? 1)
    ));

    /** Fetch the page of packages */
    protected readonly pageResult: Signal<Result<PaginatedApiResponse<Package>> | undefined> = toSignal(combineLatest({
        page: toObservable(this.page),
        queryParams: this.queryParams$
    }).pipe(
        distinctUntilChanged((prev, cur) => {
            return JSON.stringify(prev) === JSON.stringify(cur);
        }),
        switchMap(({ page, queryParams }) => {
            return this._packageService.listPotentialPackages(page, queryParams).pipe(
                // Turn into a Result
                toResult()
            );
        }),

        // Share the same result with all new subscribers
        shareReplay(1)
    ));

    protected readonly pagePinsResult: Signal<Result<ApiResponse<PackageAddress[]>> | undefined> = toSignal(combineLatest({
        queryParams: this.queryParams$
    }).pipe(
        distinctUntilChanged((prev, cur) => {
            return JSON.stringify(prev) === JSON.stringify(cur);
        }),
        switchMap(({ queryParams }) => {
            return this._packageService.getPotentialPackagesAddresses(queryParams).pipe(
                // Turn into a Result
                toResult()
            );
        }),

        // Share the same result with all new subscribers
        shareReplay(1)
    ));

    protected markers = computed(() => {
        const packageAddresses = this.pagePinsResult()?.value?.data;
        const computedMarkers: {
            addressId: string,
            title: string,
            lat: number,
            lng: number,
            packageId: string
        }[] = [];

        if (!packageAddresses) {
            return [];
        }
        packageAddresses.forEach((packageAddress) => {
            // A package with no address, or one without coordinates, simply has no pin.
            const address = packageAddress.address;
            if (!address || address.lat == null || address.lng == null) {
                return;
            }

            computedMarkers.push({
                addressId: address.id,
                title: packageAddress.reference,
                lat: +address.lat,
                lng: +address.lng,
                packageId: packageAddress.id
            });
        });

        return computedMarkers;
    });

    protected readonly markers$ = toObservable(this.markers);

    protected markerPins = signal<google.maps.marker.AdvancedMarkerElement[]>([]);

    private activeMarker: google.maps.marker.AdvancedMarkerElement | undefined;

    private markerRenderer: Renderer = {

        render: ({ count, position, markers }) => {
            // colour depends on open status of all in the cluster
            let countClosed = 0;
            let svg: HTMLElement;
            let label;
            markers!.forEach((marker) => {
                const myPackage = this.pagePinsResult()?.value?.data.find(myPackage => myPackage.id === (marker as google.maps.marker.AdvancedMarkerElement).getAttribute('packageId'));
                if (myPackage) {
                    if (!myPackage.open) {
                        countClosed++;
                    }
                }
            });
            if (countClosed === count) {
                label = `A cluster of closed packages`;
                svg = markerSvg.makeDomSvg('closed', count.toString(), label);
            } else if (countClosed === 0) {
                label = `A cluster of open packages`;
                svg = markerSvg.makeDomSvg('open', count.toString(), label);
            } else {
                label = `A cluster of packages with varied open status`;
                svg = markerSvg.makeDomSvg('mixed', count.toString(), label);
            }

            const advancedMarker = new google.maps.marker.AdvancedMarkerElement({
                position,
                content: svg,
                // adjust zIndex to be above other markers
                zIndex: 1000000 - Math.floor(position.lat() * 10000),
                title: label
            });
            this.dropMarker(advancedMarker);
            return advancedMarker;
        }
    };

    private clusterRenderer: MarkerClusterer | null = null;

    protected readonly selectedPackageIds = signal<string[] | null>(null);
    protected readonly selectedPackageId = signal<string | null>(null);
    protected readonly selectedPackage = computed(() => {
        return this.pagePinsResult()?.value?.data.find(myPackage => this.selectedPackageId() === myPackage.id);
    });

    protected readonly packageTabItems = computed(() => {
        return (this.selectedPackageIds() || []).map((packageId) => {
            const selectedPackage = this.pagePinsResult()?.value?.data.find(myPackage => packageId === myPackage.id);
            return {
                disabled: false,
                id: packageId,
                title: selectedPackage?.reference ?? ''
            };
        });
    });

    protected readonly clientNames = computed(() => {
        const selectedPackage = this.selectedPackage();
        if (!selectedPackage || !selectedPackage.clients) {
            return '';
        }

        return selectedPackage.clients
            .map(client => `${client.person.given_name} ${client.person.family_name}`)
            .join(', ');
    });

    protected readonly clientNamesShort = computed(() => {
        if (!this.selectedPackage()) {
            return '';
        }
        const numClients = (this.selectedPackage()?.clients ?? []).length;

        return this.selectedPackage()?.clients
            .slice(0, 4)
            .map(client => `${client.person.given_name} ${client.person.family_name}`)
            .join(', ') + (
            numClients > 4
                ? (' and ' + (numClients - 4).toString() + ' more.')
                : ''
        );
    });

    protected readonly clientNamesMore = computed(() => {
        if (!this.selectedPackage()) {
            return '';
        }

        return this.selectedPackage()?.clients
            .slice(4)
            .map(client => `${client.person.given_name} ${client.person.family_name}`)
            .join(', ');
    });

    /** The pagination meta data */
    protected readonly meta = computed(() => {
        // Extract the meta-data from the page result
        return this.pageResult()?.value?.meta;
    });

    options: google.maps.MapOptions = {
        center: posToLatLngLiteral(this._activeRoute.snapshot.queryParams['pos']) ?? DEFAULT_MAP_CENTER,
        zoom: this._activeRoute.snapshot.queryParams['zoom']
            ? parseFloat(this._activeRoute.snapshot.queryParams['zoom'])
            : DEFAULT_MAP_ZOOM,
        minZoom: DEFAULT_MAP_MINZOOM,
        maxZoom: DEFAULT_MAP_MAXZOOM,
        zoomControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        mapTypeControl: false,
        clickableIcons: false,
        mapId: DEFAULT_MAP_ID
    };

    private markerListeners: google.maps.MapsEventListener[] = [];

    constructor() {
        // Update the query params when the page or search query changes
        effect(() => {
            this._router.navigate([], {
                relativeTo: this._activeRoute,
                queryParams: {
                    q: this.searchQuery()
                },
                queryParamsHandling: 'merge',

                // Don't push a new history state, just replace the query param
                replaceUrl: true
            });
        });
        this._destroyRef.onDestroy(() => {
            this.intersectionObserver.disconnect();
        });

        this.filters.filterValueChanged$.pipe(
            takeUntilDestroyed(this._destroyRef)
        ).subscribe(filterDatas => {
            const queryParams = this.filters.filterValuesToQueryParams(filterDatas);
            //console.log({queryParams});
            //this.query_change_count++;
            if(this.query_change_count < 1) {
                this._router.navigate([], {
                    relativeTo: this._activeRoute,
                    queryParams: {
                        ...queryParams,
                        type: this.listType()
                    },
                    queryParamsHandling: 'merge',
                    replaceUrl: false
                });
            }
        });
    }

    async ngAfterViewInit() {
        this.markerLibrary = await google.maps.importLibrary('marker') as google.maps.MarkerLibrary;
    }

    navigateToAddPackage() {
        this._router.navigate(['/add-package']);
    }

    protected nextPage(): void {
        const meta = this.meta();

        if (meta) {
            this.setPage(meta.current_page + 1);
        }
    }

    protected previousPage(): void {
        const meta = this.meta();

        if (meta) {
            this.setPage(meta.current_page - 1);
        }
    }

    protected async setPage(page: number) {
        await this._router.navigate([], {
            queryParams: { page },
            queryParamsHandling: 'merge'
        });
    }

    updateListType(list: 'map' | 'list') {
        this.openPackageInfo([]);
        this._router.navigate([],
            {
                relativeTo: this._activeRoute,
                queryParams: { 'type': list },
                queryParamsHandling: 'merge' // remove to replace all query params by provided
            });
    }

    currentPackageTabIdChange($event: string) {
        this.selectedPackageId.update(() => $event);
    }

    editSelectedPackage() {
        const packageId = this.selectedPackageId();
        if (packageId) {
            this._router.navigate(['/packages', packageId], {
              queryParams: {
                type: 'potential-packages'
              }
            });
          }
    }

    onZoomChange() {
        // clear the selected packages away as changing zoom will change the clusters
        this.selectedPackageIds.update(() => []);
        this.selectedPackageId.update(() => null);
        this.setActive(null);
        this.mapPosToQueryParams();
    }

    async initMap(googleMap: google.maps.Map) {
        this.googleMap = googleMap;

        if (this.markerLibrary === undefined) {
            this.markerLibrary = await google.maps.importLibrary('marker') as google.maps.MarkerLibrary;
        }

        this.markers$.pipe(
            takeUntilDestroyed(this._destroyRef)
        ).subscribe(() => {
            const packageAddresses = this.pagePinsResult()?.value?.data;
            if (!packageAddresses) {
                return;
            }

            const bounds = new google.maps.LatLngBounds();

            // some cleanup
            this.markerCleanup();

            const newPins: google.maps.marker.AdvancedMarkerElement[] = [];
            packageAddresses.forEach((myPackage) => {
                const lat = myPackage.address?.lat;
                const lng = myPackage.address?.lng;
                if (lat == null || lng == null) {
                    return;
                }
                const pos = new google.maps.LatLng(+lat, +lng);
                bounds.extend(pos);
                const pin = new google.maps.marker.AdvancedMarkerElement({
                    map: this.googleMap,
                    content: markerSvg.makeDomSvg(
                        myPackage.open
                            ? 'open'
                            : 'closed'
                        , '1', `Package ${myPackage.reference}`
                    ),
                    position: pos,
                    zIndex: 1000000 - Math.floor(pos.lat() * 10000),
                    title: `Package ref: ${myPackage.reference}`
                });
                pin.setAttribute('packageId', myPackage.id);
                this.dropMarker(pin);
                const listener = pin.addListener('click', () => {
                    this.setActive(pin);
                    this.openPackageInfo([myPackage.id]);
                });
                this.markerListeners.push(listener);
                newPins.push(pin);
            });
            this.markerPins.set(newPins);

            // only if we have markers - fit the bounds
            if (newPins.length) {
                // ALWAYS ensure there is some padding around the markers
                const center = bounds.getCenter();
                if (center) {
                    const point1 = polarProjectLatLng(center!.lat(), center!.lng(), 45, 100);
                    const point2 = polarProjectLatLng(center!.lat(), center!.lng(), 225, 100);
                    bounds.extend(new google.maps.LatLng(...point1));
                    bounds.extend(new google.maps.LatLng(...point2));
                    if (!this._activeRoute.snapshot.queryParams['pos']) {
                        googleMap.fitBounds(bounds);
                    }
                }
            }

            // Add a marker clusterer to manage the markers.
            this.clusterRenderer = new MarkerClusterer({
                markers: this.markerPins(),
                map: this.googleMap,
                renderer: this.markerRenderer,
                onClusterClick: (event: google.maps.MapMouseEvent, cluster: Cluster) => {
                    if (cluster.marker instanceof google.maps.marker.AdvancedMarkerElement) {
                        this.setActive(cluster.marker);
                    }
                    this.openPackageCluster(cluster);
                },
                algorithmOptions: {
                    maxZoom: 20
                }
            });

            // if we're only showing 1 pin, open the info
            if (newPins.length === 1) {
                const delayedSourcePackageId$ = timer(500).pipe(map(() => packageAddresses[0].id));
                delayedSourcePackageId$.pipe(take(1)).subscribe((packageId) => {
                    this.openPackageInfo([packageId]);
                    this.dropMarker(newPins[0]);
                    this.activeMarker = newPins[0];
                });
            }

            // if no pins, zoom out
            if (newPins.length === 0 && this.googleMap !== undefined && !this._activeRoute.snapshot.queryParams['zoom']) {
                googleMap.setZoom(DEFAULT_MAP_ZOOM);
            }
        });
    }

    private openPackageInfo(selectedPackageIds: (string | null)[]) {
        this.selectedPackageIds.update(oldPackageIds => {
            const newPackageIds: string[] = []; // all deselected
            selectedPackageIds.forEach((packageId) => {
                if (packageId !== null) {
                    if (oldPackageIds === null || !oldPackageIds.includes(packageId)) {
                        newPackageIds.push(packageId); // select if not in current selection
                    }
                }
            });
            return newPackageIds;
        });
        if (this.selectedPackageIds()!.length > 0) {
            // opened tab should be the first one
            this.selectedPackageId.update(() => this.selectedPackageIds()![0]);
        } else {
            // nothing selected
            this.selectedPackageId.update(() => null);
        }
    }

    private openPackageCluster(cluster: Cluster) {
        if (!cluster.markers) {
            return;
        }
        const packageIds = cluster.markers.map(marker => (marker as AdvancedMarkerElement).getAttribute('packageId'));
        this.openPackageInfo(Array.from(packageIds));
    }

    handleMapError($event: ErrorEvent) {
        this.mapError.set($event);
    }

    markerCleanup(): void {
        this.markerPins().forEach((marker) => {
            marker.remove();
            marker.map = null;
        });
        this.clusterRenderer?.clearMarkers();
        this.markerPins.set([]);
        this.markerListeners.forEach((listener) => {
            google.maps.event.removeListener(listener);
        });
    }

    private setActive(pin: google.maps.marker.AdvancedMarkerElement | null) {

        if (this.activeMarker !== undefined) {
            const content = this.activeMarker.content as HTMLElement;
            content.classList.add('shrink');
            content.classList.remove('grow');
            if (pin === this.activeMarker) {
                this.activeMarker = undefined;
                return;
            }
            this.activeMarker = undefined;
        }
        if (pin) {
            if (this.activeMarker !== pin) {
                const content = pin.content as HTMLElement;
                this.activeMarker = pin;
                content.classList.add('grow');
                content.classList.remove('shrink');
            }
        }
    }

    private dropMarker(advanceMarker: google.maps.marker.AdvancedMarkerElement) {
        const content = advanceMarker.content as HTMLElement;
        content.style.opacity = '0';
        content.addEventListener('animationend', () => {
            content.classList.remove('drop');
            content.style.opacity = '1';
        }, {
            once: true
        });
        const time = Math.random();
        content.style.setProperty('--delay-time', time + 's');
        this.intersectionObserver.observe(content);
    }

    onDrag() {
        this.mapPosToQueryParams();
    }

    private mapPosToQueryParams() {
        this.options.center = this.googleMap?.getCenter() ?? DEFAULT_MAP_CENTER;
        this._router.navigate([],
            {
                relativeTo: this._activeRoute,
                queryParams: {
                    'pos': this.googleMap?.getCenter()?.lat() + ',' + this.googleMap?.getCenter()?.lng(),
                    'zoom': this.googleMap?.getZoom()
                },
                queryParamsHandling: 'merge'
            });
    }

    removeAllFilters() {
        this.filters.removeAllFilters();
    }

    toggleFilters() {
        this.filtersVisible = !this.filtersVisible;
    }
}
